import React, { useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  SectionList,
  Image,
  SectionListData,
  Pressable,
  Dimensions,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import BackArrow from "../../assets/svg/BackArrow";
import SearchIcon from "../../assets/svg/SearchIcon";
import color from "../../constants/color";
import BottomSheetComponent from "./components/BottomSheet";
import {
  BottomSheetData,
  ItemLevelSections,
  NavKey,
  Product,
  ReturnReason,
  TabData,
} from "./types";
import { useSelector } from "react-redux";
import { selectReturnDataByType } from "../../selectors/returns.selector";
import RBSheet from "react-native-raw-bottom-sheet";
import { PRODUCT_CARD_HEIGHT, itemLevelSectionsOrder } from "./constants";
import t from "@constants/localization";
import ChevronRight from "../../assets/svg/ChevronRight";

const PATH = "screens/Returns";

interface ReturnItemDetails {
  route: {
    params: {
      navKey: NavKey;
    };
  };
}

interface Section {
  key: string;
}

const ReturnItemDetails = ({ route }: ReturnItemDetails) => {
  const { navKey } = route.params;

  const navigation = useNavigation();
  const data: ItemLevelSections = useSelector((state: any) =>
    selectReturnDataByType(state, navKey),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [bottomSheetData, setBottomSheetData] = useState<BottomSheetData>({
    sectionKey: "",
    tabs: [],
    height: 0,
  });
  const bottomSheetRef = useRef<RBSheet>();

  const handleCardPress = (product: Product, section: Section) => {
    const tabs: TabData[] = [];
    if (product?.raisedByCP) {
      tabs.push({
        title: t(`${PATH}/partner`),
        name: "partner",
        screen: "ProductsList",
        data: product?.raisedByCP,
      });
    }
    if (product?.raisedByCityMall) {
      tabs.push({
        title: t(`${PATH}/cm`),
        name: "cm",
        screen: "ProductsList",
        data: product?.raisedByCityMall,
      });
    }
    const bottomSheetHeaderHeight = 180;
    const screenHeight = Dimensions.get("screen").height;
    const bottomSheetContentHeight =
      tabs.reduce((prev, current) =>
        prev.data.orders_list.length > current.data.orders_list.length
          ? prev
          : current,
      ).data.orders_list.length * PRODUCT_CARD_HEIGHT;
    const height =
      bottomSheetContentHeight > screenHeight
        ? bottomSheetContentHeight
        : bottomSheetHeaderHeight + bottomSheetContentHeight;
    setBottomSheetData({
      sectionKey: section.key,
      tabs,
      height,
    });
    bottomSheetRef?.current?.open();
  };

  const closeSheet = () => bottomSheetRef?.current?.close();

  const renderItem = ({
    item,
    section,
  }: {
    item: Product;
    section: Section;
  }) => (
    <Pressable
      android_ripple={{
        color: color.shadow,
        borderless: false,
      }}
      onPress={() => handleCardPress(item, section)}
      style={styles.card}
    >
      <Image
        source={{ uri: item.product_image }}
        resizeMode="contain"
        style={styles.cardImage}
      />
      <View style={styles.cardContent}>
        <View>
          <Text style={styles.cardTitle} ellipsizeMode="tail" numberOfLines={3}>
            {item.short_name}
          </Text>
          <Text style={styles.cardQuantity}>
            {t(`${PATH}/qty`, { qty: item.quantity })}
          </Text>
        </View>
        <View style={styles.cardContentRight}>
          <Text style={styles.refundAmount}>₹{item.refundAmount}</Text>
          <Text style={styles.refundText}>{t(`${PATH}/refund`)}</Text>
          <Text style={styles.viewDetails}>
            {t(`${PATH}/viewDetails`)}
            <View style={styles.chevronContainer}>
              <ChevronRight pathFill={color.blue} style={styles.chevronIcon} />
            </View>
          </Text>
        </View>
      </View>
    </Pressable>
  );

  const filteredSections = Object.entries(data || {})
    .sort(([keyA], [keyB]) => {
      return (
        itemLevelSectionsOrder.indexOf(keyA) -
        itemLevelSectionsOrder.indexOf(keyB)
      );
    })
    .map(([key, value]) => {
      const filteredProducts = value.products_list.filter((product: Product) =>
        product.searchable_text
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      );
      return {
        key,
        data: filteredProducts,
        qty: value.quantity,
      };
    })
    .filter((section) => section.data.length > 0);

  const renderSectionHeader = ({
    section: { key, qty },
  }: SectionListData<any, any>) => {
    const title = t(`${PATH}/${key}`);
    return (
      <Text style={styles.sectionHeader}>
        {title} ({t(`${PATH}/qty`, { qty })})
      </Text>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <BackArrow />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t(`${PATH}/ITEMS_${navKey}`)}</Text>
      </View>
      <View style={styles.searchBar}>
        <SearchIcon style={styles.searchIcon} />
        <TextInput
          placeholder={t(`${PATH}/itemSearchPlaceholder`)}
          style={styles.searchInput}
          value={searchQuery}
          onChangeText={(text) => setSearchQuery(text)}
        />
      </View>
      <SectionList
        sections={filteredSections}
        keyExtractor={(item, index) => item.slug + index}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        contentContainerStyle={styles.listContent}
        getItemLayout={(_, index) => ({
          length: 80,
          offset: 80 * index,
          index,
        })}
      />
      <BottomSheetComponent
        bottomSheetRef={bottomSheetRef}
        bottomSheetData={bottomSheetData}
        closeSheet={closeSheet}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.darkBackground,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: color.white,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: "bold",
    marginLeft: 16,
  },
  searchBar: {
    flexDirection: "row",
    alignItems: "center",
    margin: 16,
    marginBottom: 0,
    paddingHorizontal: 16,
    borderRadius: 20,
    backgroundColor: color.white,
    borderWidth: 1,
    borderColor: color.blank_grey,
    height: 40,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 12,
    fontWeight: "400",
    height: 40,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  sectionHeader: {
    fontSize: 16,
    fontWeight: "500",
    paddingVertical: 8,
  },
  card: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    backgroundColor: color.white,
    borderRadius: 8,
    marginVertical: 8,
    elevation: 4,
    height: 100,
  },
  cardImage: {
    width: 50,
    height: 50,
    marginRight: 16,
  },
  cardContent: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  cardContentRight: {
    alignItems: "center",
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: "500",
    maxWidth: "80%",
    lineHeight: 18,
    overflow: "hidden",
  },
  cardQuantity: {
    fontSize: 12,
    color: color.grey,
    fontWeight: "500",
  },
  refundAmount: {
    fontSize: 14,
    color: color.black,
    fontWeight: "600",
    textAlign: "center",
  },
  refundText: {
    fontSize: 10,
    color: color.grey,
    marginBottom: 4,
    fontWeight: "400",
    textAlign: "center",
  },
  viewDetails: {
    fontSize: 11,
    color: color.blue,
  },
  chevronContainer: {
    marginLeft: 8,
  },
  chevronIcon: {
    height: 8,
    width: 8,
  },
});

export default ReturnItemDetails;
